import React from "react";
import { Box, Typography, LinearProgress } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { SECTIONS } from "../constants/sections";
import { isSectionFilled } from "../utils/sectionUtils";

const OVERVIEW_SECTIONS = [
  "team",
  "features",
  "pricing",
  "testimonials",
  "faq",
  "caseStudies",
];

const getItemCount = (key, form) => {
  if (key === "pricing") return form[key]?.packages?.length ?? 0;
  return form[key]?.items?.length ?? 0;
};

/**
 * SectionSidebar
 *
 * Props:
 *  form              {object}
 *  onSectionClick    {fn(key)}
 *  activeSection     {string|null}  — currently open section key
 */
const SectionSidebar = ({ form, onSectionClick, activeSection }) => {
  const filledCount = SECTIONS.filter((s) =>
    isSectionFilled(s.key, form)
  ).length;
  const total = SECTIONS.length;
  const progress = Math.round((filledCount / total) * 100);

  // First incomplete section — guided mode target
  const nextIncomplete = SECTIONS.find((s) => !isSectionFilled(s.key, form));

  const renderBadge = (s, filled, isOverview, itemCount) => {
    if (!filled) {
      return (
        <Box
          sx={{
            fontSize: "9px",
            fontWeight: 600,
            lineHeight: 1,
            px: "6px",
            py: "3px",
            borderRadius: "10px",
            background: "rgba(245,158,11,0.12)",
            color: "#f59e0b",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          Empty
        </Box>
      );
    }
    if (isOverview) {
      return (
        <Box
          sx={{
            fontSize: "10px",
            fontWeight: 600,
            lineHeight: 1,
            px: "6px",
            py: "3px",
            borderRadius: "10px",
            background: "rgba(59,130,246,0.15)",
            color: "#60a5fa",
          }}
        >
          {itemCount}
        </Box>
      );
    }
    return <CheckCircleIcon sx={{ fontSize: 13, color: "#4ade80" }} />;
  };

  return (
    <Box
      sx={{
        width: 200,
        flexShrink: 0,
        borderRight: "1px solid rgba(255,255,255,0.08)",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Progress block ── */}
      <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Progress
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {filledCount}/{total}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 5,
            borderRadius: 4,
            background: "rgba(255,255,255,0.07)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 4,
              background:
                progress === 100
                  ? "#4ade80"
                  : progress >= 60
                  ? "#3b82f6"
                  : "#f59e0b",
            },
          }}
        />

        {/* Next step guide */}
        {nextIncomplete && progress < 100 && (
          <Box
            onClick={() => onSectionClick(nextIncomplete.key)}
            sx={{
              mt: 1.25,
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              cursor: "pointer",
              opacity: 0.7,
              transition: "opacity 0.15s",
              "&:hover": { opacity: 1 },
            }}
          >
            <ArrowForwardIcon sx={{ fontSize: 11, color: "#3b82f6" }} />
            <Typography variant="caption" color="#60a5fa" fontSize="10px">
              Next: {nextIncomplete.label}
            </Typography>
          </Box>
        )}

        {progress === 100 && (
          <Typography
            variant="caption"
            color="#4ade80"
            fontSize="10px"
            display="block"
            mt={1}
          >
            All sections complete
          </Typography>
        )}
      </Box>

      {/* ── Section list ── */}
      <Box sx={{ py: 1.5, px: 1.5, flex: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          sx={{
            px: 1,
            mb: 1,
            display: "block",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
          }}
        >
          Sections
        </Typography>

        {SECTIONS.map((s, idx) => {
          const filled = isSectionFilled(s.key, form);
          const isOverview = OVERVIEW_SECTIONS.includes(s.key);
          const itemCount = isOverview ? getItemCount(s.key, form) : null;
          const isActive = activeSection === s.key;
          const isNext = nextIncomplete?.key === s.key;

          return (
            <Box
              key={s.key}
              onClick={() => onSectionClick(s.key)}
              sx={{
                px: 1.5,
                py: 0.9,
                mb: 0.5,
                borderRadius: "8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                transition: "background 0.15s",
                background: isActive
                  ? "rgba(59,130,246,0.12)"
                  : isNext && !filled
                  ? "rgba(245,158,11,0.06)"
                  : "transparent",
                border: isNext && !filled
                  ? "1px solid rgba(245,158,11,0.15)"
                  : "1px solid transparent",
                "&:hover": { background: "rgba(59,130,246,0.08)" },
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Typography
                  variant="caption"
                  sx={{ fontSize: "10px", color: "text.disabled", minWidth: 14 }}
                >
                  {idx + 1}
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight={filled ? 500 : 400}
                  color={filled ? "text.primary" : "text.secondary"}
                >
                  {s.label}
                </Typography>
              </Box>

              {renderBadge(s, filled, isOverview, itemCount)}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SectionSidebar;